/**
 * @fileoverview Module for `src/pages/LetterKitsOverview.js`.
 */

import classNames from 'classnames';
import { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';

import LetterKitIcon from '../letter-kits/LetterKitIcon';
import LetterKitRequestForm from '../letter-kits/LetterKitRequestForm';
import SupportVF from '../components/SupportVF';
import VerifyEmailRequest from '../VerifyEmailRequest';
import { get } from '../lib/apiClient';
import { isAuthenticated } from '../lib/user';

import '../scss/pages/_letter-kits-overview.scss';

import envelopeIcon from '../images/vf_icon_envelope.png';
import letterIcon from '../images/vf_icon_letter.png';
import stampIcon from '../images/vf_icon_stamp.png';

/**
 * Letter kit program overview and request page.
 *
 * @returns {JSX.Element} Letter kits view.
 */
function LetterKitsOverview() {
  /**
   * Shared setting query wrapper for kit-limit counters.
   *
   * @param {string} queryKey - React Query key suffix.
   * @param {string} endpoint - Setting endpoint name.
   * @returns {Object} React Query result.
   */
  const useKitLimit = (queryKey, endpoint) => useQuery(
    ['settings', queryKey],
    async () => await get(`/v1/settings/${ endpoint }`),
    { select: (limit) => (limit ? Math.max(limit.value - (limit.currentCount ? limit.currentCount : 0), 0) : 0) },
  );
  const { data: availableLetterKitCount } = useKitLimit(
    'availableLetterKitCount',
    'LETTERKIT_LIMIT',
  );
  const { data: availableStampKitCount } = useKitLimit(
    'availableStampKitCount',
    'STAMPKIT_LIMIT',
  );
  const { data: kitRequestLimit } = useKitLimit(
    'kitRequestLimit',
    'KIT_REQUEST_LIMIT',
  );
  // const { data: flyerRequestLimit } = useKitLimit(
  //   'flyerRequestLimit',
  //   'FLYER_REQUEST_LIMIT',
  // );

  /**
   * Returns whether any kit inventory remains.
   *
   * @returns {boolean} True when at least one kit type is available.
   */
  function isAnyKitAvailable() {
    return availableLetterKitCount !== 0 || availableStampKitCount !== 0;
  }

  return (
    <Fragment>
      <Helmet>
        <title> Letter Kits</title>
      </Helmet>

      <main className='col-lg-8 mx-auto my-5' id='letter-kits-overview'>
        <section className='container mb-4 overview'>
          <div className='row'>
            <div className='col'>
              <h1>Request a stamp or letter kit</h1>

              <p>
                Vote Forward letters are a proven effective way for grassroots volunteers to boost voter turnout. But for some volunteers, there are practical and financial barriers to writing letters, including access to a printer, stamps, or envelopes.
                That&apos;s why we offer <strong>stamp and letter kits!</strong>
              </p>
            </div>
          </div>
        </section>

        <section className='container how-it-works mb-5'>
          <div className='row'>
            <div className='col'>
              <a id='how-it-works'>
                <h2 className='h3 headline'>
                  How it works
                </h2>
              </a>
              <p>
                Request a letter or stamp kit below, and we&apos;ll mail you a physical kit. Letter kits include 20 stamps, 20 pre-printed letters, and 20 envelopes. Stamp kits just include 20 stamps. <strong>When you are approved for either type of kit, the voters are automatically added to your Vote Forward account - after you complete the form below, there is no need to adopt voters again!</strong> After your kit request is approved, just log in to your Vote Forward dashboard and find the letter bundle associated with your kit.
                <strong> Note: For our PA New Voter Guides campaign, a full kit includes 20 printed guides and 40 stickers, no envelopes. Stamp kits are still available, including 20 stamps.</strong>
              </p>
            </div>
          </div>

          <div className='row'>
            <LetterKitIcon alt='icon of stamp on dark blue background' caption='20 stamps' src={ stampIcon } />
            <LetterKitIcon
              alt='icon of letter on dark blue background'
              caption='20 printed letters (optional)'
              src={ letterIcon }
            />
            <LetterKitIcon
              alt='icon of envelope on dark blue background'
              caption='20 blank envelopes (optional)'
              src={ envelopeIcon }
            />
          </div>

          <div className='row'>
            <div className='col'>
              <p>
                Just prepare the letters, mail them on the mail date, and mark them &ldquo;sent&rdquo;
                on your Vote Forward dashboard once you&rsquo;ve done so.
              </p>
            </div>
          </div>
        </section>

        <section className='request-kit container mb-4'>
          <div className='row'>
            <div className='col'>
              <a id='request-a-kit'>
                <h2 className='h3 headline'>
                  Current Inventory
                </h2>
                <h3>
                  Full kits (envelopes, letters, stamps):
                  { ' ' }
                  { availableLetterKitCount }
                </h3>
                <h3>
                  Postage-only kits (just the stamps):
                  { ' ' }
                  { availableStampKitCount }
                </h3>
              </a>
            </div>
          </div>
          <div
            className={ classNames(
              'row',
              {
                'mb-1': isAnyKitAvailable(),
                'mb-5': !isAnyKitAvailable(),
              },
            ) }
          >
            <div className='col'>
              {
                !isAnyKitAvailable()
                  ? (
                    <Fragment>
                      <p>
                        Thank you for your interest in writing Vote Forward letters. We&rsquo;ve received requests for the
                        as many kits as are currently available, so we cannot accept additional requests at this
                        time. If you requested a kit and received an approval email, but have not yet received your kit,
                        it is being assembled and will be mailed to you soon.
                      </p>
                      <p>
                        If you&rsquo;d like to help supply more letter kits to more volunteers,
                        { ' ' }
                        <a href='https://secure.actblue.com/donate/vote-forward-letter-kits?refcode=letterkits_landing'>
                          please consider making a donation
                        </a>
                        { ' ' }
                        to support this and all of Vote Forward&rsquo;s programs.
                      </p>
                    </Fragment>
                    )
                  : null
              }
            </div>
          </div>
          {
            isAnyKitAvailable() && isAuthenticated()
              ? (
                <div className='row'>
                  <div className='col'>
                    <LetterKitRequestForm
                      isFullKitAvailable={ availableLetterKitCount !== 0 }
                      isPostageOnlyKitAvailable={ availableStampKitCount !== 0 }
                      kitRequestLimit={ kitRequestLimit }
                    />
                  </div>
                </div>
                )
              : null
          }
          {
            isAnyKitAvailable() && !isAuthenticated()
              ? (
                <div className='row'>
                  <div className='col'>
                    <p>
                      While your application is very welcome, we ask that you refrain from applying if you don&rsquo;t need the support, so that other volunteers can make use of the program. Volunteers are able to request one letter or stamp kit every 30 days.
                    </p>

                    <VerifyEmailRequest hideFooter hideHeader hideMasthead purpose='register.user' rememberLocation />
                  </div>
                </div>
                )
              : null
          }
        </section>

        <SupportVF
          appeal='If you can, please consider making a donation to help support Vote Forward&rsquo;s work and the letter kits program. Each letter kit costs approximately $20 to produce and distribute.'
          appealHeadline='Support Vote Forward & This Program'
          donationRefCode='letterkits_landing'
          donationSlug='vote-forward-letter-kits'
        />
      </main>
    </Fragment>
  );
}

export default LetterKitsOverview;
